import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';
import { ARCHETYPES } from './archetypeData';
import BreathingOrb from '../Premium/BreathingOrb';
import sonicCues from '../Premium/sonicCues';

const ArchetypeSelection = ({ onSelect, onBack }) => {
  const [selectedId, setSelectedId] = useState(null);

  const handleSelect = (archetypeId) => {
    sonicCues.threshold();
    setSelectedId(archetypeId);
    // Brief delay for animation before proceeding
    setTimeout(() => {
      onSelect(archetypeId);
    }, 500);
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto overflow-x-hidden"
      style={{
        background: "radial-gradient(ellipse at 50% 0%, #18213A 0%, #0a1224 50%, #03060A 100%)",
      }}
      data-testid="archetype-selection"
    >
      {/* Signature Moment · Breathing Orb behind the choice. Sets the
          tone: this is a sacred selection, not a form. */}
      <BreathingOrb brainState="alpha" tone="gold" size={780} />

      <div className="px-6 py-8 pb-32 relative z-10">
        {/* Back button */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => { sonicCues.tap(); onBack(); }}
          className="rec-chip mb-8"
          data-testid="archetype-back"
        >
          <ChevronLeft size={14} />
          Back
        </motion.button>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-12"
        >
          <p
            className="uppercase tracking-[0.32em] mb-3"
            style={{
              color: "#D7BB7A",
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.58rem",
            }}
          >
            Threshold · 1 of 5
          </p>
          <h1
            className="text-3xl font-light mb-3 italic"
            style={{
              color: "#F3EDDD",
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              letterSpacing: "0.04em",
            }}
          >
            Choose your reality
          </h1>
          <p
            className="italic"
            style={{
              color: "rgba(243, 237, 221, 0.55)",
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: "0.95rem",
            }}
          >
            Not becoming… returning.
          </p>
        </motion.div>

        {/* Archetype Cards · rec-surface premium treatment */}
        <div className="space-y-3 max-w-xl mx-auto">
          {Object.values(ARCHETYPES).map((archetype, index) => {
            const active = selectedId === archetype.id;
            return (
              <motion.button
                key={archetype.id}
                initial={{ opacity: 0, x: -24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15 + index * 0.07, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -2 }}
                onClick={() => handleSelect(archetype.id)}
                className="rec-surface w-full p-5 text-left relative overflow-hidden group transition-all duration-500"
                style={{
                  borderColor: active ? archetype.color : "rgba(215, 187, 122, 0.16)",
                  boxShadow: active
                    ? `0 0 28px ${archetype.glowColor}, 0 0 0 1px ${archetype.color}aa inset`
                    : undefined,
                }}
                data-testid={`archetype-${archetype.id}`}
              >
                {/* Glow on selection */}
                <AnimatePresence>
                  {active && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.85 }}
                      animate={{ opacity: 0.8, scale: 1.4 }}
                      exit={{ opacity: 0 }}
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        background: `radial-gradient(circle at center, ${archetype.glowColor} 0%, transparent 70%)`,
                      }}
                    />
                  )}
                </AnimatePresence>

                <div className="relative z-10 flex items-center gap-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl transition-transform group-hover:scale-110"
                    style={{ backgroundColor: `${archetype.color}1f`, border: `1px solid ${archetype.color}44` }}
                  >
                    {archetype.emoji}
                  </div>
                  <div className="flex-1">
                    <p
                      className="mb-0.5 italic"
                      style={{
                        color: "#F3EDDD",
                        fontFamily: '"Cormorant Garamond", serif',
                        fontSize: "1.2rem",
                        lineHeight: 1.3,
                      }}
                    >
                      {archetype.name}
                    </p>
                    <p
                      className="italic opacity-70"
                      style={{
                        color: "rgba(243, 237, 221, 0.55)",
                        fontFamily: '"Cormorant Garamond", serif',
                        fontSize: "0.92rem",
                      }}
                    >
                      {archetype.tagline}
                    </p>
                  </div>

                  {/* Subtle arrow indicator */}
                  <motion.div
                    animate={{ opacity: active ? 1 : 0.32, x: active ? 0 : -6 }}
                    transition={{ duration: 0.5 }}
                  >
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: `${archetype.color}18`, border: `1px solid ${archetype.color}33` }}
                    >
                      <span style={{ color: archetype.color, fontSize: "0.95rem" }}>→</span>
                    </div>
                  </motion.div>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ArchetypeSelection;
